import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { dateFormat } from '@/utils/dateFormat';
import { STEP_RESULT_PATTERNS } from '../../constant';
import type { JobChangeProcessDetail } from '../../types/JobChangeProcess';
import { useSetAtom } from 'jotai';
import { currentSelectjobChangeProcessLogStatusAtom } from '../../atoms/jobChangeProcess';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { Radio } from '@/components/Radio';
import { Textarea } from '@/components/Textarea';
import type { AgentApiJobChangeProcessesLog } from '@/types/serializers';
import useUpdateJobChangeProcess from '../../api/useUpdateJobChangeProcess';
import { Button } from '@/components/Button';

type Props = {
  currentSelectjobChangeProcessId: number;
  logs: AgentApiJobChangeProcessesLog[];
  statusOpen: () => void;
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<JobChangeProcessDetail, Error>>;
  isSidebarOpen: boolean;
  createdAt?: string;
};

type DisplayLog = AgentApiJobChangeProcessesLog & { isEntry?: boolean };

export default function JobChangeProcessLogList({
  currentSelectjobChangeProcessId,
  logs,
  statusOpen,
  refetch,
  isSidebarOpen,
  createdAt,
}: Props) {
  const setCurrentSelectjobChangeProcessLogStatus = useSetAtom(
    currentSelectjobChangeProcessLogStatusAtom
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEdit, setIsEdit] = useState({ id: 0, open: false });
  const [result, setResult] = useState('');
  const [message, setMessage] = useState('');
  const [showMessageArea, setShowMessageArea] = useState(false);

  const { mutateAsync: updateJobChangeProcess } = useUpdateJobChangeProcess();

  const onClickDelete = (log: AgentApiJobChangeProcessesLog) => {
    setCurrentSelectjobChangeProcessLogStatus({
      result: '',
      interviewedAt: null,
      jobChangeProcessId: currentSelectjobChangeProcessId,
      latestLogId: log.id,
      type: 'delete',
      refetch: refetch,
    });
    statusOpen();
  };

  const onClickEdit = (log: AgentApiJobChangeProcessesLog) => {
    setCurrentSelectjobChangeProcessLogStatus({
      result: result,
      interviewedAt: null,
      jobChangeProcessId: currentSelectjobChangeProcessId,
      latestLogId: log.id,
      type: 'edit',
      refetch: refetch,
    });
    statusOpen();
  };

  const onChangeResult = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setResult(value);
  };

  const onChangeMessage = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.currentTarget.value;
    setMessage(value);
  };

  const onBlurMessage = () => {
    const latestLog = logs[logs.length - 1];
    if (latestLog) {
      updateJobChangeProcess({
        jobChangeProcessId: currentSelectjobChangeProcessId,
        latestLogId: latestLog.id,
        interviewed_at: null,
        contact_note: message,
      })
        .then(() => {
          refetch();
        })
        .catch((error) => {
          console.error('Failed to update contact note:', error);
        });
    }
  };

  useEffect(() => {
    if (!isSidebarOpen) {
      setIsMenuOpen(false);
      setIsEdit({ id: 0, open: false });
      setShowMessageArea(false);
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    if (logs.length > 0) {
      const latestLog = logs[logs.length - 1];
      setMessage(latestLog.contact_note || '');
    }
  }, [logs]);

  const entryLog: DisplayLog = {
    id: -1,
    order_number: 0,
    interview_type: null,
    interview_step: null,
    is_final_interview: false,
    result: null,
    contact_note: null,
    interviewed_at: createdAt ?? '',
    result_inserted_at: null,
    step_name: 'エントリー',
    isEntry: true,
  };

  const displayLogs: DisplayLog[] = [entryLog, ...logs];

  return (
    <div className="flex flex-col gap-2">
      {/* ログリスト */}
      <div className="relative space-y-2 pl-1">
        {displayLogs.map((l, key) => {
          const isEntry = l.isEntry === true;
          const stepResult = !isEntry
            ? STEP_RESULT_PATTERNS.find((r) => r.value === l.result)
            : undefined;
          const isLatest = key === displayLogs.length - 1;
          const displayStepName = l.step_name;
          const logDate = l.result_inserted_at ?? (isEntry ? l.interviewed_at : null);

          return (
            <div key={key} className="relative flex gap-4" style={{ minHeight: '36px' }}>
              {/* タイムラインインジケーター */}
              <div className="relative flex flex-col items-center">
                <div className="z-10 mt-2 size-1 rounded-full bg-[#262626]"></div>
                {!isLatest && <div className="absolute top-2 -bottom-4 w-px bg-[#262626]"></div>}
                {isLatest && (
                  <div
                    className="absolute top-2 bottom-0 w-px"
                    style={{
                      borderLeft: '1px dashed #262626',
                    }}
                  ></div>
                )}
              </div>

              {/* コンテンツエリア */}
              <div className="flex-1" style={{ paddingBottom: '8px' }}>
                {isLatest && showMessageArea ? (
                  /* 最新ステップでメッセージエリア表示時 */
                  <div className="flex flex-col gap-1">
                    {/* ステップ情報 */}
                    <div className="flex w-[334px] items-center justify-between">
                      <div className="flex items-center gap-1">
                        <p className="w-16 text-xs text-[#262626]">{displayStepName}</p>
                        {stepResult?.label && (
                          <div className="rounded-full bg-[#E7E7E7] px-[10px] py-[2px]">
                            <p className="text-xs text-[#262626]">{stepResult.label}</p>
                          </div>
                        )}
                        {logDate && (
                          <p className="ml-14 text-xs text-[#262626]">
                            {dateFormat(new Date(logDate))}
                          </p>
                        )}
                      </div>

                      {!isEntry && (
                        /* ドロップダウンメニュー */
                        <div className="relative">
                          <button
                            type="button"
                            className="flex size-5 items-center justify-center rounded border border-border bg-white hover:opacity-60"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                          >
                            <img
                              src="/agent/job_change_processes/icon-more.svg"
                              width={12}
                              height={12}
                              alt=""
                            />
                          </button>

                          {isMenuOpen && (
                            <div className="absolute top-6 right-0 z-10 w-[163px] rounded bg-white shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]">
                              <button
                                type="button"
                                className="flex h-8 w-full items-center px-3 text-sm text-[#262626] hover:bg-gray-50"
                                onClick={() => {
                                  setIsEdit({ id: l.id, open: true });
                                  setIsMenuOpen(false);
                                }}
                              >
                                編集する
                              </button>
                              <button
                                type="button"
                                className="flex h-8 w-full items-center px-3 text-sm text-red-600 hover:bg-gray-50"
                                onClick={() => {
                                  setIsMenuOpen(false);
                                  onClickDelete(l);
                                }}
                              >
                                削除する
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* メッセージエリア */}
                    {!isEntry && (
                      <div className="h-32 w-[334px]">
                        <div className="mb-1 flex items-center gap-1">
                          <p className="pl-5 text-sm font-bold text-[#262626]">
                            求職者への共有事項
                          </p>
                          <button
                            type="button"
                            className="flex size-4 items-center justify-center"
                            title="ツールチップ"
                          >
                            <img
                              src="/agent/job_change_processes/icon-tooltip.svg"
                              width={16}
                              height={16}
                              alt=""
                            />
                          </button>
                        </div>
                        <div className="relative">
                          <Textarea
                            className="h-24 resize-none p-2 text-sm"
                            placeholder="テキスト&#10;テキスト&#10;テキスト"
                            value={message}
                            onChange={onChangeMessage}
                            onBlur={onBlurMessage}
                          />
                          <div className="absolute right-1 bottom-1 flex gap-1">
                            <div className="size-2 border border-[#6D6D6D] bg-white"></div>
                            <div className="size-1 border border-[#6D6D6D] bg-white"></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* 通常表示 */
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <p className="w-24 text-xs text-[#262626]">{displayStepName}</p>

                      {!isEntry && isEdit.id === l.id && isEdit.open ? (
                        /* 編集モード時はstepResultを表示 */
                        <>
                          <div className="flex w-24 shrink-0 justify-start">
                            {stepResult?.label ? (
                              <div className="inline-flex rounded-full bg-[#E7E7E7] px-[10px] py-[2px]">
                                <p className="text-xs text-[#262626]">{stepResult.label}</p>
                              </div>
                            ) : (
                              <div className="h-[22px] w-full"></div>
                            )}
                          </div>
                          {logDate && (
                            <p className="text-xs text-[#262626]">
                              {dateFormat(new Date(logDate))}
                            </p>
                          )}
                        </>
                      ) : (
                        /* 通常表示 */
                        <>
                          <div className="flex w-24 shrink-0 justify-start">
                            {stepResult?.label ? (
                              <div className="inline-flex rounded-full bg-[#E7E7E7] px-[10px] py-[2px]">
                                <p className="text-xs text-[#262626]">{stepResult.label}</p>
                              </div>
                            ) : (
                              <div className="h-[22px] w-full"></div>
                            )}
                          </div>
                          {logDate && (
                            <p className="text-xs text-[#262626]">
                              {dateFormat(new Date(logDate))}
                            </p>
                          )}

                          {/* 最新ステップのアクション */}
                          {!isEntry && isLatest && (
                            <div className="ml-auto flex items-center gap-2">
                              {/* ドロップダウンメニュー */}
                              <div className="relative">
                                <button
                                  type="button"
                                  className="flex size-5 items-center justify-center rounded border border-border bg-white hover:opacity-60"
                                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                                >
                                  <img
                                    src="/agent/job_change_processes/icon-more.svg"
                                    width={12}
                                    height={12}
                                    alt=""
                                  />
                                </button>

                                {isMenuOpen && (
                                  <div className="absolute top-6 right-0 z-10 w-[163px] rounded bg-white shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]">
                                    <button
                                      type="button"
                                      className="flex h-8 w-full items-center px-3 text-sm text-[#262626] hover:bg-gray-50"
                                      onClick={() => {
                                        setIsEdit({ id: l.id, open: true });
                                        setIsMenuOpen(false);
                                      }}
                                    >
                                      編集する
                                    </button>
                                    <button
                                      type="button"
                                      className="flex h-8 w-full items-center px-3 text-sm text-red-600 hover:bg-gray-50"
                                      onClick={() => {
                                        setIsMenuOpen(false);
                                        onClickDelete(l);
                                      }}
                                    >
                                      削除する
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* 編集モードのUIをstepResultの下に表示 */}
                    {!isEntry && isEdit.id === l.id && isEdit.open && (
                      <div className="flex items-center gap-3">
                        {STEP_RESULT_PATTERNS.map((s, radioKey) => (
                          <Radio
                            key={radioKey}
                            value={s.value}
                            onChange={onChangeResult}
                            checked={s.value === result}
                            labelClassName="text-[10px]"
                          >
                            {s.label}
                          </Radio>
                        ))}
                        <Button
                          type="button"
                          color="secondary"
                          size="sm"
                          disabled={result === ''}
                          onClick={() => {
                            setIsEdit({ id: 0, open: false });
                            onClickEdit(l);
                          }}
                        >
                          保存
                        </Button>
                      </div>
                    )}

                    {/* 最新ステップには常にメッセージエリアを表示 */}
                    {!isEntry && isLatest && (
                      <div className="h-32 w-[334px]">
                        <div className="mt-1 flex items-center gap-1">
                          <button
                            type="button"
                            className="flex size-4 items-center justify-center"
                            title="ツールチップ"
                          >
                            <img
                              src="/agent/job_change_processes/icon-tooltip.svg"
                              width={16}
                              height={16}
                              alt=""
                            />
                          </button>
                          <p className="text-sm font-bold text-[#262626]">求職者への共有事項</p>
                        </div>
                        <div className="relative mt-1">
                          <Textarea
                            className="h-24 resize-none p-2 text-sm"
                            placeholder=""
                            value={message}
                            onChange={onChangeMessage}
                            onBlur={onBlurMessage}
                          />
                          <div className="absolute right-1 bottom-1 flex gap-1">
                            <div className="size-2 border border-[#6D6D6D] bg-white"></div>
                            <div className="size-1 border border-[#6D6D6D] bg-white"></div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 過去のステップでcontact_noteがある場合のみツールチップアイコン */}
                    {!isEntry && !isLatest && l.contact_note && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="flex size-4 items-center justify-center"
                          onClick={() => setShowMessageArea(!showMessageArea)}
                          title="メッセージを表示/非表示"
                        >
                          <img
                            src="/agent/job_change_processes/icon-tooltip.svg"
                            width={16}
                            height={16}
                            alt=""
                          />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
